// Service for fetching metrics from Prometheus
const PROMETHEUS_URL = import.meta.env.VITE_PROMETHEUS_URL || 'http://localhost:9090';

export interface AgentMetrics {
  agentName: string;
  processingRate: number;
  errorRate: number;
  successRate: number;
  avgResponseTime: number;
  tokensPerMinute: number;
  costPerHour: number;
  kafkaLag: number;
  cacheHitRate: number;
  memoryUsage: number;
  cpuUsage: number;
  uptime: number;
}

export interface PrometheusQueryResult {
  status: string;
  data: {
    resultType: string;
    result: Array<{
      metric: Record<string, string>;
      value?: [number, string];
      values?: Array<[number, string]>;
    }>;
  };
}

class PrometheusService {
  private async query(query: string): Promise<PrometheusQueryResult> {
    const url = `${PROMETHEUS_URL}/api/v1/query?query=${encodeURIComponent(query)}`;
    const response = await fetch(url);
    
    if (!response.ok) {
      throw new Error(`Prometheus query failed: ${response.statusText}`);
    }
    
    return response.json();
  }

  private async queryRange(query: string, start: number, end: number, step: number): Promise<PrometheusQueryResult> {
    const url = `${PROMETHEUS_URL}/api/v1/query_range?query=${encodeURIComponent(query)}&start=${start}&end=${end}&step=${step}`;
    const response = await fetch(url);
    
    if (!response.ok) {
      throw new Error(`Prometheus query failed: ${response.statusText}`);
    }
    
    return response.json();
  }

  private extractValue(result: PrometheusQueryResult, defaultValue = 0): number {
    if (result.data.result.length > 0 && result.data.result[0].value) {
      return parseFloat(result.data.result[0].value[1]);
    }
    return defaultValue;
  }

  private extractValueByLabel(result: PrometheusQueryResult, label: string, labelValue: string, defaultValue = 0): number {
    const match = result.data.result.find(r => r.metric[label] === labelValue);
    if (match && match.value) {
      return parseFloat(match.value[1]);
    }
    return defaultValue;
  }

  async getAgentMetrics(agentName: string): Promise<AgentMetrics> {
    try {
      // Run all queries in parallel for better performance
      const [
        processingRateResult,
        errorRateResult,
        successRateResult,
        responseTimeResult,
        tokensResult,
        costResult,
        kafkaLagResult,
        cacheHitRateResult,
        memoryResult,
        cpuResult,
        uptimeResult
      ] = await Promise.all([
        // Processing rate (messages per second)
        this.query(`rate(agent_messages_processed_total{agent_name="${agentName}"}[5m])`),
        
        // Error rate
        this.query(`rate(agent_errors_total{agent_name="${agentName}"}[5m])`),
        
        // Success rate
        this.query(`
          sum(rate(agent_messages_processed_total{agent_name="${agentName}",status="success"}[5m])) / 
          sum(rate(agent_messages_processed_total{agent_name="${agentName}"}[5m])) * 100
        `),
        
        // Average response time (p50)
        this.query(`
          histogram_quantile(0.5, 
            sum(rate(agent_message_processing_duration_seconds_bucket{agent_name="${agentName}"}[5m])) 
            by (le)
          )
        `),
        
        // Tokens per minute
        this.query(`sum(rate(agent_llm_tokens_total{agent_name="${agentName}"}[5m])) * 60`),
        
        // Cost per hour
        this.query(`sum(rate(agent_llm_cost_dollars_total{agent_name="${agentName}"}[1h])) * 3600`),
        
        // Kafka consumer lag
        this.query(`max(agent_kafka_consumer_lag{agent_name="${agentName}"})`),
        
        // Cache hit rate
        this.query(`
          sum(rate(agent_cache_requests_total{agent_name="${agentName}",result="hit"}[5m])) / 
          sum(rate(agent_cache_requests_total{agent_name="${agentName}"}[5m])) * 100
        `),
        
        // Memory usage (heap used)
        this.query(`agent_memory_bytes{agent_name="${agentName}",type="heapUsed"}`),
        
        // CPU usage
        this.query(`agent_cpu_usage_percent{agent_name="${agentName}"}`),
        
        // Uptime
        this.query(`process_start_time_seconds{agent_name="${agentName}"}`)
      ]);

      const now = Date.now() / 1000;
      const startTime = this.extractValue(uptimeResult);
      const uptime = startTime > 0 ? now - startTime : 0;

      return {
        agentName,
        processingRate: this.extractValue(processingRateResult),
        errorRate: this.extractValue(errorRateResult),
        successRate: this.extractValue(successRateResult, 100),
        avgResponseTime: this.extractValue(responseTimeResult),
        tokensPerMinute: this.extractValue(tokensResult),
        costPerHour: this.extractValue(costResult),
        kafkaLag: this.extractValue(kafkaLagResult),
        cacheHitRate: this.extractValue(cacheHitRateResult, 0),
        memoryUsage: this.extractValue(memoryResult),
        cpuUsage: this.extractValue(cpuResult),
        uptime
      };
    } catch (error) {
      console.error(`Failed to fetch metrics for agent ${agentName}:`, error);
      // Return default metrics on error
      return {
        agentName,
        processingRate: 0,
        errorRate: 0,
        successRate: 0,
        avgResponseTime: 0,
        tokensPerMinute: 0,
        costPerHour: 0,
        kafkaLag: 0,
        cacheHitRate: 0,
        memoryUsage: 0,
        cpuUsage: 0,
        uptime: 0
      };
    }
  }

  async getAllAgentMetrics(): Promise<AgentMetrics[]> {
    try {
      // First, get list of all agents from the up metric
      const agentsResult = await this.query('up{job=~".*agent.*"}');
      const agentNames = [...new Set(agentsResult.data.result
        .map(r => r.metric.agent_name)
        .filter(Boolean))];

      // Fetch metrics for all agents in parallel
      const metricsPromises = agentNames.map(name => this.getAgentMetrics(name));
      return await Promise.all(metricsPromises);
    } catch (error) {
      console.error('Failed to fetch all agent metrics:', error);
      return [];
    }
  }

  async getAgentTimeSeries(agentName: string, metric: string, duration = '1h', step = 60): Promise<Array<[number, number]>> {
    try {
      const now = Math.floor(Date.now() / 1000);
      const durationSeconds = this.parseDuration(duration);
      const start = now - durationSeconds;

      let query = '';
      switch (metric) {
        case 'processingRate':
          query = `rate(agent_messages_processed_total{agent_name="${agentName}"}[5m])`;
          break;
        case 'errorRate':
          query = `rate(agent_errors_total{agent_name="${agentName}"}[5m])`;
          break;
        case 'responseTime':
          query = `histogram_quantile(0.5, sum(rate(agent_message_processing_duration_seconds_bucket{agent_name="${agentName}"}[5m])) by (le))`;
          break;
        case 'tokenUsage':
          query = `rate(agent_llm_tokens_total{agent_name="${agentName}"}[5m]) * 60`;
          break;
        case 'cost':
          query = `rate(agent_llm_cost_dollars_total{agent_name="${agentName}"}[5m]) * 60`;
          break;
        case 'memory':
          query = `agent_memory_bytes{agent_name="${agentName}",type="heapUsed"}`;
          break;
        case 'cpu':
          query = `agent_cpu_usage_percent{agent_name="${agentName}"}`;
          break;
        default:
          throw new Error(`Unknown metric: ${metric}`);
      }

      const result = await this.queryRange(query, start, now, step);
      
      if (result.data.result.length > 0 && result.data.result[0].values) {
        return result.data.result[0].values.map(([timestamp, value]) => [
          timestamp * 1000, // Convert to milliseconds
          parseFloat(value)
        ]);
      }
      
      return [];
    } catch (error) {
      console.error(`Failed to fetch time series for ${agentName}:`, error);
      return [];
    }
  }

  private parseDuration(duration: string): number {
    const match = duration.match(/^(\d+)([hdms])$/);
    if (!match) {
      throw new Error(`Invalid duration format: ${duration}`);
    }

    const value = parseInt(match[1]);
    const unit = match[2];

    switch (unit) {
      case 's': return value;
      case 'm': return value * 60;
      case 'h': return value * 3600;
      case 'd': return value * 86400;
      default: throw new Error(`Unknown duration unit: ${unit}`);
    }
  }
}

export const prometheusService = new PrometheusService();